import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ChevronDown } from "lucide-react"
import { useSearchParams } from "react-router"
import { toastApiError } from "../../../api/apiError"
import {
  useArticleCompliance,
  useUpdateArticleCompliance,
} from "../../../api/articlesApi"
import type { ArticleComplianceOut } from "../../../api/types/masterData"
import { intlLocale } from "../../../utils/date"
import {
  normalizeFrameworkCode,
  regulationLabel,
} from "../../../utils/regulationLabel"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"
import { McPill } from "../../shared/McPill"

type McPillTone = "success" | "attention" | "danger" | "muted"

type TranslateFn = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

type StatusMeta = { labelKey: TranslationKeys; tone: McPillTone }

// The canonical verdict vocabulary — backend regulatory_content.VERDICT_INDEX,
// enforced by ArticleComplianceCreateIn/UpdateIn (anything else is a 422). It
// is therefore the only set the dropdown may offer. Labels reuse the review
// screen's MC_DECL_VERDICT_* keys so one verdict reads the same everywhere.
const STATUS_OPTIONS: ({ value: string } & StatusMeta)[] = [
  {
    value: "compliant",
    labelKey: "MC_DECL_VERDICT_COMPLIANT",
    tone: "success",
  },
  {
    value: "compliant_with_conditions",
    labelKey: "MC_DECL_VERDICT_COMPLIANT_WITH_CONDITIONS",
    tone: "attention",
  },
  {
    value: "non_compliant",
    labelKey: "MC_DECL_VERDICT_NON_COMPLIANT",
    tone: "danger",
  },
  { value: "pending", labelKey: "MC_DECL_VERDICT_PENDING", tone: "muted" },
  { value: "unknown", labelKey: "MC_DECL_VERDICT_UNKNOWN", tone: "muted" },
]

// Read-only: codes the spreadsheet-import path still writes. Existing rows hold
// them, so they must keep rendering with their own label + tone — but they are
// NOT offered in the dropdown (the write endpoints reject them). Tones follow
// the backend classifier's class: exempt / compliant_with_exemption are
// conditional (attention), not_applicable is indeterminate (muted).
const LEGACY_STATUS_META: Record<string, StatusMeta> = {
  exempt: { labelKey: "MC_MD_STATUS_EXEMPT", tone: "attention" },
  compliant_with_exemption: {
    labelKey: "MC_MD_STATUS_COMPLIANT_EXEMPT",
    tone: "attention",
  },
  not_applicable: { labelKey: "MC_MD_STATUS_NA", tone: "muted" },
}

function statusMeta(status: string): StatusMeta | undefined {
  return (
    STATUS_OPTIONS.find((o) => o.value === status) ?? LEGACY_STATUS_META[status]
  )
}

// mc_article_compliance.source — written by ArticleComplianceUpdateIn's
// default ('manual'), the spreadsheet-import commit path ('import'), and
// declaration-review approval ('declaration'). Unknown values fall back to
// the raw string rather than disappearing.
const SOURCE_LABEL_KEYS: Record<string, TranslationKeys> = {
  manual: "MC_MD_SOURCE_MANUAL",
  import: "MC_MD_SOURCE_IMPORT",
  declaration: "MC_MD_SOURCE_DECLARATION",
}

function sourceLabel(source: string, translate: TranslateFn): string {
  const key = SOURCE_LABEL_KEYS[source]
  return key ? translate(key) : source
}

function complianceStatusTone(status: string): McPillTone {
  // Every canonical verdict and every legacy code that has a label is covered
  // above — reaching "muted" here means an unmapped code, which the pill then
  // renders neutrally rather than colouring it as if it had been read.
  return statusMeta(status)?.tone ?? "muted"
}

function StatusCell({ row }: { row: ArticleComplianceOut }) {
  const { mutate, isPending } = useUpdateArticleCompliance()
  const { translate } = useTranslation()

  const label = (status: string) => {
    const meta = statusMeta(status)
    // Unknown status (neither a canonical verdict nor a legacy import code)
    // falls back to the shared "unknown verdict" label rather than a
    // humanized raw code — matches the review screen's own fallback.
    return meta
      ? translate(meta.labelKey)
      : translate("MC_DECL_VERDICT_UNKNOWN")
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-agent-target="article.compliance-pill"
        disabled={isPending}
        onClick={(e) => e.stopPropagation()}
        className="group flex items-center gap-1"
      >
        <McPill tone={complianceStatusTone(row.status)} dim={isPending}>
          {label(row.status)}
        </McPill>
        <ChevronDown
          size={12}
          className="text-grey5 opacity-0 transition-opacity group-hover:opacity-100"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
        {STATUS_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            disabled={opt.value === row.status}
            onSelect={() =>
              mutate(
                {
                  articleId: row.articleId,
                  complianceId: row.id,
                  changes: { status: opt.value, source: "manual" },
                },
                {
                  onError: (err) => {
                    toastApiError(err, translate, "MC_MD_SAVE_ERROR")
                  },
                },
              )
            }
          >
            <McPill tone={opt.tone}>{translate(opt.labelKey)}</McPill>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function ArticleComplianceSection() {
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const { translate, currentLanguage } = useTranslation()

  const { data: verdicts = [], isLoading } = useArticleCompliance(id)

  const columns: SubTableColumn<ArticleComplianceOut>[] = [
    {
      key: "supplier",
      header: translate("MC_MD_PANEL_COL_SUPPLIER"),
      cell: (row) =>
        row.supplierName ? (
          <span className="truncate">{row.supplierName}</span>
        ) : (
          <span className="italic text-grey5">
            {translate("MC_MD_COMPLIANCE_NO_SUPPLIER")}
          </span>
        ),
      className: "w-36",
    },
    {
      key: "regulation",
      header: translate("MC_MD_PANEL_COL_REGULATION"),
      cell: (row) => {
        // mc_regulations.name (ROHS, SVHC, REACH, REACH_ANNEX_XVII, …) is the
        // registry's UPPER_SNAKE token, not display copy — normalize it to
        // the shared canonical code before looking up the human label.
        const name = row.regulationName || row.regulationId
        return (
          <span className="truncate">
            {regulationLabel(normalizeFrameworkCode(name), name, translate)}
          </span>
        )
      },
      className: "w-36",
    },
    {
      key: "status",
      header: translate("MC_MD_PANEL_COL_STATUS"),
      cell: (row) => <StatusCell row={row} />,
      className: "w-40",
    },
    {
      key: "source",
      header: translate("MC_MD_PANEL_COL_SOURCE"),
      cell: (row) => (
        <span className="text-grey5">{sourceLabel(row.source, translate)}</span>
      ),
      className: "w-28",
      hideBelow: "480px",
    },
    {
      key: "updatedAt",
      header: translate("MC_MD_PANEL_COL_UPDATED"),
      cell: (row) =>
        row.updatedAt ? (
          <span className="font-mono text-grey5">
            {new Date(row.updatedAt).toLocaleDateString(
              intlLocale(currentLanguage),
            )}
          </span>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-28",
      hideBelow: "480px",
    },
  ]

  return (
    <EntitySubTable
      title={translate("MC_MD_PANEL_COMPLIANCE_TITLE")}
      items={verdicts}
      columns={columns}
      keyOf={(row) => `${row.supplierId ?? "import"}-${row.regulationId}`}
      hasMore={false}
      onFetchMore={() => Promise.resolve()}
      isLoading={isLoading}
      total={isLoading ? undefined : verdicts.length}
    />
  )
}
